import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { raisedPanelSx } from '@/shared/ui/panelShadowStyles';
import { layoutMeasures } from '@/theme/layoutMeasures';
import { s } from '@/theme/tokens/spacing';
import { Box, Inline, PageShell, Stack, StatusBanner, Surface, Text } from '@/ui';
import { Button } from '@/ui/primitives/Button';
import type { AuthSession } from '@/auth/types';

type AuthErrorBoundaryState = { hasError: boolean; message?: string };
const fallbackContentTextSx = { fontSize: '15px' };

type AuthErrorContext = { path: string; userId: string | null; orgId: string | null };

type AuthErrorBoundaryInnerProps = {
  children: ReactNode;
  context: AuthErrorContext;
  onReset: () => void;
  onSignOut: () => void;
};

class AuthErrorBoundaryInner extends Component<
  AuthErrorBoundaryInnerProps,
  AuthErrorBoundaryState
> {
  state: AuthErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): AuthErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // UI fallback is handled by getDerivedStateFromError.
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { context, onReset, onSignOut } = this.props;
    return (
      <Box
        sx={{
          minHeight: layoutMeasures.viewport.contentMinHeight,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <PageShell
          paddingX="lg"
          paddingY="lg"
          sx={{ width: 'fit-content', maxWidth: 'min(100%, 56rem)' }}
        >
          <Surface padding="lg" sx={[raisedPanelSx, { width: 'fit-content', maxWidth: '100%' }]}>
            <Stack gap="md" align="center" sx={{ width: 'fit-content', maxWidth: '100%' }}>
              <StatusBanner
                tone="red"
                message="We hit a problem while updating your session. Try again or sign in."
                sx={{ width: 'fit-content', maxWidth: '100%' }}
              />
              {this.state.message ? (
                <Text role="body" color="textSecondary">
                  {this.state.message}
                </Text>
              ) : null}
              <Stack gap="sm" align="center">
                <Text role="caption" color="textSecondary" sx={fallbackContentTextSx}>
                  Route: {context.path}
                </Text>
                <Text role="caption" color="textSecondary" sx={fallbackContentTextSx}>
                  User: {context.userId ?? 'signed out'} · Org: {context.orgId ?? 'personal'}
                </Text>
              </Stack>
              <Inline
                gap="sm"
                wrap="wrap"
                align="center"
                justify="center"
                sx={{ width: 'fit-content', maxWidth: '100%', marginTop: s('sm') }}
              >
                <>
                  <Button variant="primary" onClick={onReset}>
                    Retry
                  </Button>
                  <Button variant="secondary" onClick={onSignOut}>
                    Sign In Again
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      window.location.reload();
                    }}
                  >
                    Reload
                  </Button>
                </>
              </Inline>
            </Stack>
          </Surface>
        </PageShell>
      </Box>
    );
  }
}

export const AuthErrorBoundary = ({
  children,
  session,
  onSignOut,
}: {
  children: ReactNode;
  session: AuthSession | null;
  onSignOut: () => void;
}): ReactNode => {
  const location = useLocation();
  const navigate = useNavigate();
  const [resetKey, setResetKey] = useState(0);
  const lastKeyRef = useRef<string>('');

  const context = useMemo<AuthErrorContext>(
    () => ({
      path: location.pathname,
      userId: session?.userId ?? null,
      orgId: session?.orgId ?? null,
    }),
    [location.pathname, session?.orgId, session?.userId],
  );

  useEffect(() => {
    const nextKey = `${context.path}:${context.userId ?? 'anon'}:${context.orgId ?? 'none'}`;
    if (lastKeyRef.current !== nextKey) {
      lastKeyRef.current = nextKey;
      setResetKey((prev) => prev + 1);
    }
  }, [context.orgId, context.path, context.userId]);

  return (
    <AuthErrorBoundaryInner
      key={resetKey}
      context={context}
      onReset={() => {
        setResetKey((prev) => prev + 1);
      }}
      onSignOut={() => {
        onSignOut();
        void navigate('/session-expired', { replace: true, state: { from: location.pathname } });
      }}
    >
      {children}
    </AuthErrorBoundaryInner>
  );
};
